import { useState, useEffect } from 'react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Mail, MessageSquare, Pencil } from 'lucide-react';
import { transactionalTemplateApi } from '@/lib/api';
import type { TransactionalTemplate } from '@/lib/api';
import { MessageTemplateEditor } from '@/components/MessageTemplateEditor';

// Group templates by document type for display
const DOCUMENT_GROUPS = [
  {
    title: 'Invoice',
    purposes: ['send_invoice', 'reminder_invoice'],
  },
  {
    title: 'Quote (No Deposit)',
    purposes: ['send_quote_no_deposit', 'reminder_quote_no_deposit'],
  },
  {
    title: 'Quote (With Deposit)',
    purposes: ['send_quote_deposit', 'reminder_quote_deposit'],
  },
  {
    title: 'Purchase Order',
    purposes: ['send_purchase_order'],
  },
  {
    title: 'Credit Memo',
    purposes: ['send_credit_memo'],
  },
];

export function MessageTemplates() {
  const { nav, businessId } = useBusinessNav();
  const [templates, setTemplates] = useState<TransactionalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<TransactionalTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [businessId]);

  const loadTemplates = async () => {
    if (!businessId) return;
    setLoading(true);
    const response = await transactionalTemplateApi.list(businessId);
    if (response.data) {
      setTemplates(response.data);
    }
    setLoading(false);
  };

  const handleSave = async (subject: string, body: string) => {
    if (!businessId || !editingTemplate) return;

    const response = await transactionalTemplateApi.update(
      businessId,
      editingTemplate.purpose,
      editingTemplate.medium,
      { subject, body }
    );

    if (response.data) {
      // Update the template in state
      setTemplates(prev =>
        prev.map(t =>
          t.purpose === editingTemplate.purpose && t.medium === editingTemplate.medium
            ? response.data!
            : t
        )
      );
      setEditingTemplate(null);
    }
  };

  const handleReset = async () => {
    if (!businessId || !editingTemplate) return;

    const response = await transactionalTemplateApi.resetToDefault(
      businessId,
      editingTemplate.purpose,
      editingTemplate.medium
    );

    if (response.data) {
      // Update the template in state
      setTemplates(prev =>
        prev.map(t =>
          t.purpose === editingTemplate.purpose && t.medium === editingTemplate.medium
            ? response.data!
            : t
        )
      );
      setEditingTemplate(null);
    }
  };

  const getTemplatesByPurpose = (purpose: string) => {
    return templates.filter(t => t.purpose === purpose);
  };

  return (
    <div className="px-6 pt-6 pb-6">
      {/* Header */}
      <div className="space-y-1 mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-2 -ml-2 text-muted-foreground"
          onClick={() => nav('/settings')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Settings
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Message Templates</h1>
        <p className="text-sm text-muted-foreground">
          Customize the emails and SMS messages sent to your clients.
        </p>
      </div>

      {/* Template List */}
      {loading ? (
        <div className="space-y-6">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-6 w-32 mb-4" />
                <div className="space-y-3">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {DOCUMENT_GROUPS.map(group => {
            const groupTemplates = group.purposes.flatMap(p => getTemplatesByPurpose(p));
            if (groupTemplates.length === 0) return null;

            return (
              <Card key={group.title}>
                <CardContent className="pt-6">
                  <h3 className="font-medium mb-4">{group.title}</h3>
                  <div className="space-y-3">
                    {groupTemplates.map(template => (
                      <button
                        key={`${template.purpose}-${template.medium}`}
                        onClick={() => setEditingTemplate(template)}
                        className="w-full flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                            {template.medium === 'email' ? (
                              <Mail className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <MessageSquare className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">
                                {template.purpose_label}
                              </span>
                              <Badge variant="secondary" className="text-xs">
                                {template.medium === 'email' ? 'Email' : 'SMS'}
                              </Badge>
                              {template.is_custom && (
                                <Badge variant="default" className="text-xs">
                                  Custom
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                              {template.medium === 'email' && template.subject
                                ? template.subject
                                : template.body.slice(0, 60) + (template.body.length > 60 ? '...' : '')}
                            </p>
                          </div>
                        </div>
                        <Pencil className="h-4 w-4 text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Editor Dialog */}
      {editingTemplate && (
        <MessageTemplateEditor
          template={editingTemplate}
          open={!!editingTemplate}
          onOpenChange={(open) => !open && setEditingTemplate(null)}
          onSave={handleSave}
          onReset={handleReset}
        />
      )}
    </div>
  );
}
